import { useState } from "react";
import { AlertTriangle, Eye, EyeOff, Plus, X } from "lucide-react";

import type { AuthMethod } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/Page";
import { cn } from "@/lib/utils";
import {
  HTTP_METHODS,
  hostOfURL,
  METHOD_BLURBS,
  METHOD_LABELS,
  METHODS_WITH_BODY,
  SELECT_CLS,
  TEXTAREA_CLS,
  type HTTPMethod,
} from "./shared";
import type { ConnectForm, EnvRow } from "./useConnectForm";

// KindToggle — the fresh-connect kind chooser used inside the reconnect/edit
// dialog path (the wizard page uses its step-1 chooser instead). Switching away
// from HTTP coerces a stale browser-OAuth selection (it's HTTP-only).
export function KindToggle({ form }: { form: ConnectForm }) {
  const choose = (k: "http" | "openapi" | "stdio") => {
    form.setKind(k);
    if (k !== "http" && form.method === "oauth2_authorization_code") form.setMethod("bearer");
  };
  return (
    <Field label="Connect by">
      <Segmented
        value={form.kind}
        onChange={choose}
        options={[
          { value: "http", label: "MCP server URL" },
          { value: "openapi", label: "API (OpenAPI / cURL)" },
          { value: "stdio", label: "npm / PyPI" },
        ]}
      />
    </Field>
  );
}

// NameField — the integration's handle (an identifier, not a display name).
export function NameField({ form }: { form: ConnectForm }) {
  const locked = !!form.reconnect || form.isEdit;
  return (
    <Field
      label="Name"
      htmlFor="conn-name"
      required
      error={form.nameError ?? undefined}
      hint={locked ? "The integration's identifier — can't be changed." : "A short identifier, e.g. stripe or github."}
    >
      <Input
        id="conn-name"
        placeholder="stripe"
        value={form.name}
        onChange={(e) => form.setName(e.target.value)}
        disabled={locked}
        required
        aria-invalid={form.nameError ? true : undefined}
      />
    </Field>
  );
}

// HttpFields — the MCP server URL.
export function HttpFields({ form }: { form: ConnectForm }) {
  return (
    <Field label="Server URL" htmlFor="conn-url" required error={form.urlError ?? undefined} hint="The MCP server's address.">
      <Input
        id="conn-url"
        placeholder="https://mcp.stripe.com"
        value={form.url}
        onChange={(e) => form.setUrl(e.target.value)}
        required
        aria-invalid={form.urlError ? true : undefined}
      />
    </Field>
  );
}

// A small segmented control used for the OpenAPI source sub-choice.
function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div
      className={cn("grid gap-1 rounded-md border bg-muted/30 p-1", options.length === 2 ? "grid-cols-2" : "grid-cols-3")}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={cn(
            "rounded px-3 py-1.5 text-sm font-medium transition-colors",
            value === o.value ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// HeaderRows — the reusable header key/value editor (form headers + custom-header auth).
function HeaderRows({
  rows,
  setRows,
  keyPlaceholder = "Header",
  secret = false,
}: {
  rows: { key: string; value: string }[];
  setRows: (r: { key: string; value: string }[]) => void;
  keyPlaceholder?: string;
  secret?: boolean;
}) {
  return (
    <div className="space-y-2">
      {rows.map((h, i) => (
        <div key={i} className="flex gap-2">
          <Input
            placeholder={keyPlaceholder}
            value={h.key}
            onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}
          />
          <Input
            type={secret ? "password" : "text"}
            placeholder="Value"
            value={h.value}
            onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            aria-label="Remove"
            onClick={() => setRows(rows.filter((_, j) => j !== i))}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setRows([...rows, { key: "", value: "" }])}>
        <Plus className="h-3.5 w-3.5" /> Add row
      </Button>
    </div>
  );
}

// AuthFields — the inputs the chosen auth method needs.
export function AuthFields({ form }: { form: ConnectForm }) {
  switch (form.method) {
    case "none":
      return <p className="text-sm text-muted-foreground">This integration is called without any credentials.</p>;
    case "bearer":
      return (
        <Field label="Token" hint="Stored encrypted; never shown again.">
          <Input type="password" placeholder="sk-…" value={form.token} onChange={(e) => form.setToken(e.target.value)} />
        </Field>
      );
    case "api_key":
      return (
        <div className="grid gap-3 sm:grid-cols-[1fr_1.5fr]">
          <Field label="Header name">
            <Input value={form.apiHeader} onChange={(e) => form.setApiHeader(e.target.value)} placeholder="X-API-Key" />
          </Field>
          <Field label="Value" hint="Stored encrypted.">
            <Input type="password" value={form.apiValue} onChange={(e) => form.setApiValue(e.target.value)} />
          </Field>
        </div>
      );
    case "basic":
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Username">
            <Input value={form.basicUser} onChange={(e) => form.setBasicUser(e.target.value)} />
          </Field>
          <Field label="Password">
            <Input type="password" value={form.basicPass} onChange={(e) => form.setBasicPass(e.target.value)} />
          </Field>
        </div>
      );
    case "headers":
      return (
        <Field label="Custom headers" hint="Values are stored encrypted.">
          <HeaderRows rows={form.headers} setRows={form.setHeaders} secret />
        </Field>
      );
    case "oauth2_client_credentials":
      return (
        <div className="space-y-3">
          <Field label="Token URL">
            <Input
              value={form.tokenURL}
              onChange={(e) => form.setTokenURL(e.target.value)}
              placeholder="https://auth.example.com/oauth/token"
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Client ID">
              <Input value={form.clientID} onChange={(e) => form.setClientID(e.target.value)} />
            </Field>
            <Field label="Client secret">
              <Input type="password" value={form.clientSecret} onChange={(e) => form.setClientSecret(e.target.value)} />
            </Field>
          </div>
          <Field label="Scopes" hint="Space-separated, optional.">
            <Input value={form.scopes} onChange={(e) => form.setScopes(e.target.value)} placeholder="read write" />
          </Field>
        </div>
      );
    case "oauth2_authorization_code":
      return (
        <div className="space-y-3">
          <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            We'll find the sign-in endpoints and register automatically. Clicking{" "}
            <strong className="text-foreground">Continue to sign-in</strong> opens the provider's consent screen. The
            fields below are only needed if the provider doesn't support automatic registration.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Client ID" hint="Optional.">
              <Input value={form.clientID} onChange={(e) => form.setClientID(e.target.value)} />
            </Field>
            <Field label="Client secret" hint="Optional.">
              <Input type="password" value={form.clientSecret} onChange={(e) => form.setClientSecret(e.target.value)} />
            </Field>
          </div>
          <Field label="Scopes" hint="Space-separated, optional.">
            <Input value={form.scopes} onChange={(e) => form.setScopes(e.target.value)} placeholder="offline_access …" />
          </Field>
        </div>
      );
  }
}

// AuthPicker — the auth-method dropdown + its fields (HTTP/OpenAPI only).
export function AuthPicker({ form }: { form: ConnectForm }) {
  return (
    <div className="space-y-4">
      <Field label="Authentication" htmlFor="conn-method" hint={METHOD_BLURBS[form.method]}>
        <select
          id="conn-method"
          value={form.method}
          onChange={(e) => form.setMethod(e.target.value as AuthMethod)}
          className={SELECT_CLS}
        >
          {form.methodOptions.map((m) => (
            <option key={m} value={m}>
              {METHOD_LABELS[m]}
            </option>
          ))}
        </select>
      </Field>
      <AuthFields form={form} />
    </div>
  );
}

// OpenAPIFields — define the API by a friendly form, an OpenAPI spec, or a cURL.
export function OpenAPIFields({ form }: { form: ConnectForm }) {
  return (
    <div className="space-y-4">
      <Field label="Define by">
        <Segmented
          value={form.source}
          onChange={form.setSource}
          options={[
            { value: "form", label: "Form" },
            { value: "spec", label: "OpenAPI spec" },
            { value: "curl", label: "cURL" },
          ]}
        />
      </Field>

      {form.source === "form" ? (
        <>
          <div className="grid gap-2 sm:grid-cols-[7rem_1fr]">
            <Field label="Method">
              <select
                value={form.formMethod}
                onChange={(e) => form.setFormMethod(e.target.value as HTTPMethod)}
                className={SELECT_CLS}
              >
                {HTTP_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Request URL"
              error={form.formUrl.length > 0 && !form.formUrlValid ? "Enter a URL starting with http:// or https://" : undefined}
            >
              <Input
                placeholder="https://api.example.com/v1/things?limit=10"
                value={form.formUrl}
                onChange={(e) => form.setFormUrl(e.target.value)}
                aria-invalid={form.formUrl.length > 0 && !form.formUrlValid ? true : undefined}
              />
            </Field>
          </div>
          <Field label="Headers" hint="Don't add auth headers here — set credentials in the Authentication step.">
            <HeaderRows rows={form.formHeaders} setRows={form.setFormHeaders} />
          </Field>
          {METHODS_WITH_BODY.includes(form.formMethod) && (
            <Field label="Request body" hint="Optional.">
              <textarea
                className={cn(TEXTAREA_CLS, "min-h-[100px]")}
                placeholder={'{ "key": "value" }'}
                value={form.formBody}
                onChange={(e) => form.setFormBody(e.target.value)}
              />
            </Field>
          )}
        </>
      ) : form.source === "spec" ? (
        <>
          <Field label="Spec URL">
            <Input
              placeholder="https://api.example.com/openapi.json"
              value={form.specUrl}
              onChange={(e) => form.setSpecUrl(e.target.value)}
            />
          </Field>
          <Field label="Or paste the spec" hint="JSON or YAML.">
            <textarea
              className={cn(TEXTAREA_CLS, "min-h-[120px]")}
              placeholder={'{ "openapi": "3.0.0", … }'}
              value={form.specText}
              onChange={(e) => form.setSpecText(e.target.value)}
            />
          </Field>
          <Field label="Base URL" hint="Optional — derived from the spec's servers[] if blank.">
            <Input
              placeholder="https://api.example.com"
              value={form.baseUrl}
              onChange={(e) => form.setBaseUrl(e.target.value)}
            />
          </Field>
        </>
      ) : (
        <Field label="Paste a cURL command">
          <textarea
            className={cn(TEXTAREA_CLS, "min-h-[120px]")}
            placeholder="curl https://api.example.com/v1/things -H 'Authorization: Bearer …'"
            value={form.curl}
            onChange={(e) => form.setCurl(e.target.value)}
          />
        </Field>
      )}

      {form.source !== "spec" && (
        <Field
          label="Instructions for the agent"
          hint="Optional — the agent reads this to build the request (a single example carries no schema)."
        >
          <textarea
            className={cn(TEXTAREA_CLS, "min-h-[88px] font-sans")}
            placeholder={
              "What this does and what to send. e.g.\nCreates an order. Body: customer_id (string, required), items [{sku, qty}]. Returns the order id."
            }
            value={form.instructions}
            onChange={(e) => form.setInstructions(e.target.value)}
          />
        </Field>
      )}
    </div>
  );
}

// EnvVarRow — one row of the unified env-var editor. Each row is either a
// plaintext value or a saved secret; a per-row toggle switches between them.
// Plaintext values are masked by default (env often holds credentials) with an
// eye reveal. A secret row picks the saved secret + names the env var the package
// reads it from; how it's delivered (file path vs value) is inferred from the
// secret's own kind.
function EnvVarRow({
  form,
  row,
  onChange,
  onRemove,
}: {
  form: ConnectForm;
  row: EnvRow;
  onChange: (next: EnvRow) => void;
  onRemove: () => void;
}) {
  const [reveal, setReveal] = useState(false);
  const setType = (t: "plaintext" | "secret") => {
    if (t === row.type) return;
    const name = row.type === "plaintext" ? row.key : row.varName;
    onChange(
      t === "plaintext"
        ? { type: "plaintext", key: name, value: "" }
        : { type: "secret", varName: name, secretId: "", mode: "env" },
    );
  };
  const name = row.type === "plaintext" ? row.key : row.varName;
  const setName = (v: string) =>
    onChange(row.type === "plaintext" ? { ...row, key: v } : { ...row, varName: v });

  return (
    <div className="space-y-1 rounded-md border bg-muted/20 p-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Segmented
          value={row.type}
          onChange={setType}
          options={[
            { value: "plaintext", label: "Value" },
            { value: "secret", label: "Secret" },
          ]}
        />
        <Input
          aria-label="Variable name"
          className="font-mono text-xs sm:w-44"
          placeholder="KEY"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {row.type === "plaintext" ? (
          <div className="relative flex-1">
            <Input
              type={reveal ? "text" : "password"}
              aria-label="Value"
              className="font-mono text-xs pr-9"
              placeholder="value"
              value={row.value}
              onChange={(e) => onChange({ ...row, value: e.target.value })}
            />
            <button
              type="button"
              aria-label={reveal ? "Hide value" : "Show value"}
              className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground hover:text-foreground"
              onClick={() => setReveal((v) => !v)}
            >
              {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </div>
        ) : (
          <select
            aria-label="Secret"
            className={cn(SELECT_CLS, "flex-1")}
            value={row.secretId}
            onChange={(e) => {
              const picked = form.availSecrets.find((s) => s.id === e.target.value);
              onChange({ ...row, secretId: e.target.value, mode: picked?.kind === "file" ? "file" : "env" });
            }}
          >
            <option value="">Choose a secret…</option>
            {form.availSecrets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.kind})
              </option>
            ))}
          </select>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 self-end sm:self-auto"
          aria-label="Remove variable"
          onClick={onRemove}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      {row.type === "secret" &&
        row.secretId &&
        name.trim() &&
        (() => {
          const sec = form.availSecrets.find((s) => s.id === row.secretId);
          const isFile = (sec ? sec.kind : row.mode) === "file";
          const preview = isFile ? `path to ${sec?.file_name || "the saved file"}` : "the secret's value";
          return (
            <p className="px-0.5 text-xs text-muted-foreground">
              <span className="font-mono text-foreground">{name.trim()}</span> = {preview}{" "}
              <span className="opacity-70">(we set this automatically)</span>
            </p>
          );
        })()}
    </div>
  );
}

// StdioEnvTable — the unified env-var editor. One typed row list: each row is a
// plaintext value (inline sealed env) or a saved secret (vault attachment). On
// edit, plaintext values prefill (masked by default); secret bytes never do.
function StdioEnvTable({ form }: { form: ConnectForm }) {
  const rows = form.envRows;
  if (form.envEditError) {
    return (
      <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
        {form.envEditError}
      </p>
    );
  }
  if (form.isStdio && form.isStdioEdit && !form.envEditReady) {
    return (
      <p className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Spinner className="h-3 w-3" /> Loading environment variables…
      </p>
    );
  }
  const update = (i: number, next: EnvRow) => form.setEnvRows(rows.map((x, j) => (j === i ? next : x)));
  const remove = (i: number) => form.setEnvRows(rows.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Each variable is either a plain value or a saved secret. Use a secret for credentials like a service-account
        file — manage saved secrets on the <span className="font-medium text-foreground">Secrets</span> page.
      </p>
      {rows.map((row, i) => (
        <EnvVarRow key={i} form={form} row={row} onChange={(next) => update(i, next)} onRemove={() => remove(i)} />
      ))}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => form.setEnvRows([...rows, { type: "plaintext", key: "", value: "" }])}
        >
          <Plus className="h-3.5 w-3.5" /> Add variable
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => form.setEnvRows([...rows, { type: "secret", varName: "", secretId: "", mode: "env" }])}
        >
          <Plus className="h-3.5 w-3.5" /> Add secret
        </Button>
      </div>
    </div>
  );
}

// StdioFields — the npm/PyPI package configuration.
export function StdioFields({ form }: { form: ConnectForm }) {
  const py = form.stdioRuntime === "python";
  return (
    <div className="space-y-4">
      {form.freshConnect && (
        <Field label="Package source">
          <Segmented
            value={form.stdioRuntime}
            onChange={form.setStdioRuntime}
            options={[
              { value: "npm", label: "npm (Node)" },
              { value: "python", label: "PyPI (Python)" },
            ]}
          />
        </Field>
      )}
      <Field
        label={py ? "PyPI package" : "npm package"}
        htmlFor="conn-pkg"
        required
        hint={py ? "Installed with uv." : "Installed with npm (scripts disabled)."}
      >
        <Input
          id="conn-pkg"
          placeholder={py ? "mcp-server-fetch" : "@modelcontextprotocol/server-everything"}
          value={form.stdioPkg}
          onChange={(e) => form.setStdioPkg(e.target.value)}
          required
        />
      </Field>
      <Field label="Version" htmlFor="conn-pkgv" hint="Optional — defaults to the latest.">
        <Input
          id="conn-pkgv"
          placeholder="latest"
          value={form.stdioVersion}
          onChange={(e) => form.setStdioVersion(e.target.value)}
        />
      </Field>
    </div>
  );
}

// StdioArgs — extra CLI args. Fresh = one-per-line textarea; edit = per-position
// rows so a secret embedded in an arg is never re-typed.
function StdioArgs({ form }: { form: ConnectForm }) {
  if (form.envEditError) {
    return (
      <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
        {form.envEditError}
      </p>
    );
  }
  if (!form.isStdioEdit) {
    return (
      <Field label="Arguments" htmlFor="conn-args" hint="Optional — one per line. Stored encrypted.">
        <textarea
          id="conn-args"
          className={cn(TEXTAREA_CLS, "min-h-[88px]")}
          placeholder={"--readOnly\n--connectionString=…\n/path/to/allowed/dir"}
          value={form.stdioArgs}
          onChange={(e) => form.setStdioArgs(e.target.value)}
        />
      </Field>
    );
  }
  if (!form.envEditReady)
    return (
      <p className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Spinner className="h-3 w-3" /> Loading arguments…
      </p>
    );
  const rows = form.stdioArgRows;
  return (
    <Field label="Arguments" hint="Existing arguments show with values masked — leave blank to keep, type to replace.">
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-xs text-muted-foreground">No arguments configured.</p>}
        {rows.map((row, i) => (
          <div key={i} className="flex gap-2">
            <Input
              className="font-mono text-xs"
              placeholder={row.locked ? (row.redacted ?? "•••• unchanged") : "--flag or --flag=value"}
              value={row.value}
              onChange={(e) => form.setStdioArgRows(rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              aria-label="Remove argument"
              onClick={() => form.setStdioArgRows(rows.filter((_, j) => j !== i))}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => form.setStdioArgRows([...rows, { value: "" }])}
        >
          <Plus className="h-3.5 w-3.5" /> Add argument
        </Button>
      </div>
    </Field>
  );
}

// ConnectorBanner — the needs-OAuth-app setup warning for a catalog connector.
function ConnectorBanner({ form }: { form: ConnectForm }) {
  const c = form.connector;
  if (!c?.needsOAuthApp || !c.mcpUrl) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-warning">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        {c.name} needs a one-time OAuth app set up for <span className="font-mono">{hostOfURL(c.mcpUrl)}</span> before
        sign-in will work. Ask your workspace admin if you're not sure.
      </span>
    </div>
  );
}

// Convenience: which kind-specific section to render.
export function KindFields({ form }: { form: ConnectForm }) {
  if (form.isOpenAPI) return <OpenAPIFields form={form} />;
  if (form.isStdio) return <StdioFields form={form} />;
  return <HttpFields form={form} />;
}

// Export the stdio sub-blocks so the wizard can place them in their own Sections.
export { ConnectorBanner, StdioEnvTable, StdioArgs };
